// Apolo IDE — useViewport (Sprint v9 M0).
//
// Primitivos numéricos do viewport + flags de plataforma. Complementa
// useBreakpoint (que retorna label string) com width/height + isMobile +
// isIOS + isStandalone — coisas que componentes mobile precisam mas o
// label sozinho não dá.
//
// SSR-safe (retorna defaults conservadores no servidor), debounced 150ms
// pra evitar layout thrash em rotação rápida ou resize livre desktop.

import { useEffect, useState } from 'react';
import { BREAKPOINTS } from '../lib/breakpoints.js';

const DEBOUNCE_MS = 150;

function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ se passa por macOS no UA — checa touch também.
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document)
  );
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  // Chrome/Edge/Firefox PWA + Safari macOS
  const mql = window.matchMedia?.('(display-mode: standalone)');
  if (mql && mql.matches) return true;
  // Safari iOS legacy (não suporta display-mode standalone até iOS 17)
  return Boolean(window.navigator && /** @type {{ standalone?: boolean }} */ (window.navigator).standalone);
}

function snapshot() {
  if (typeof window === 'undefined') {
    return {
      width: 1280,
      height: 800,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isIOS: false,
      isStandalone: false,
    };
  }
  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    isMobile: width < BREAKPOINTS.lg,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    isIOS: detectIOS(),
    isStandalone: detectStandalone(),
  };
}

/**
 * @returns {{
 *   width: number;
 *   height: number;
 *   isMobile: boolean;
 *   isTablet: boolean;
 *   isDesktop: boolean;
 *   isIOS: boolean;
 *   isStandalone: boolean;
 * }}
 */
export function useViewport() {
  const [vp, setVp] = useState(snapshot);

  useEffect(() => {
    let timer = null;
    function onResize() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setVp(snapshot());
        timer = null;
      }, DEBOUNCE_MS);
    }
    window.addEventListener('resize', onResize);
    // Captura mudanças de display-mode (standalone toggle quando PWA instala).
    const mql = window.matchMedia?.('(display-mode: standalone)');
    const onModeChange = () => setVp(snapshot());
    mql?.addEventListener?.('change', onModeChange);

    return () => {
      window.removeEventListener('resize', onResize);
      mql?.removeEventListener?.('change', onModeChange);
      if (timer) clearTimeout(timer);
    };
  }, []);

  return vp;
}
